import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import StyledCustomModal from "../../../../packages/CustomModal/StyledCustomModal";
import IconButton from "../../../../packages/IconButton";
import CloseIcon from "../../../../packages/SVGs/CloseIcon";
import WPButton from "../../../../packages/WPButton";
import WPHeading from "../../../../packages/WPHeading";

import UserLicenseDetails from "./UserLicenseDetails";

import { APP_ROUTES, USER_ROLES } from "../../../../constants/global.constants";

import { AdvisorIcon } from "../../../../assets/icons";

import { API_URLS } from "../../../../network/appUrls";

import "./index.scss";

const UserSettings = ({ close, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { userName, userEmail, companyName, userLicenses, userRole } =
    useSelector((state) => state);

  const basePath = API_URLS.basePath;

  const handleCloseDialog = () => {
    onClose?.() || close?.();
  };

  const handleGoToLicenseManagement = () => {
    const path = `${basePath}${APP_ROUTES.LICENSE_MANAGEMENT}`;
    handleCloseDialog();
    navigate(path);
  };

  const initials = useMemo(() => {
    const parts = (userName || "")
      .split(" ")
      .map((namePart) => namePart.trim())
      .filter(Boolean);

    if (!parts.length) return "U";

    return parts
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("");
  }, [userName]);

  const normalizePermissionCategory = (category = "") => {
    const normalized = category.toString().trim().toLowerCase();
    if (normalized === "technology" || normalized === "technologies") {
      return "Technology";
    }
    if (normalized === "feature" || normalized === "features") {
      return "Feature";
    }
    if (
      normalized === "product" ||
      normalized === "products" ||
      normalized === "product-list"
    ) {
      return "Product";
    }
    return category;
  };

  const normalizeLicensePermissions = (license) => {
    const flatPermissions =
      license?.licensePermissions || license?.permissions || [];
    if (Array.isArray(flatPermissions) && flatPermissions.length) {
      return flatPermissions.map((permission, idx) => ({
        permissionId: permission?.permissionId ?? idx,
        name: permission?.name ?? permission?.label ?? "",
        category: normalizePermissionCategory(permission?.category),
      }));
    }

    const toPermission = (items, category) =>
      (items || []).map((item, idx) => ({
        permissionId: item?.permissionId ?? item?.id ?? `${category}-${idx}`,
        name: item?.name ?? item?.label ?? item,
        category,
      }));

    return [
      ...toPermission(license?.technologies, "Technology"),
      ...toPermission(license?.features, "Feature"),
      ...toPermission(license?.products, "Product"),
    ];
  };

  const normalizeLicenseResponse = (response) => {
    const payload = response?.data ?? response;
    const records = payload?.licenses || [];

    if (!Array.isArray(records)) {
      return [];
    }

    return records.map((license, idx) => ({
      id: license?.id ?? license?.licenseId ?? idx + 1,
      name:
        license?.name ??
        license?.licenseName ??
        license?.licenseType ??
        `License ${idx + 1}`,
      assignedDate: license?.assignedDate ?? license?.assignedOn ?? null,
      expiryDate: license?.expiryDate ?? license?.expiredOn ?? null,
      licensePermissions: normalizeLicensePermissions(license),
      licenseStatus: license?.licenseStatus ?? null,
    }));
  };

  const licenses = useMemo(
    () => normalizeLicenseResponse({ licenses: userLicenses }),
    [userLicenses],
  );

  return (
    <StyledCustomModal
      show
      centered
      width="960px"
      scrollable
      className="user-settings-modal"
      onHide={handleCloseDialog}
    >
      <div className="modalWaveHeader">
        <div className="w-100 headerContent">
          <div className="d-flex headerTopRow">
            <WPHeading
              className="welcomeTitle"
              id="welcome-to-ro-advisor"
              size="xxLarge"
              fontNoto
              secondary
              color="var(--du-color-white)"
            >
              {t("licenseProfile.welcomeTitle")}
            </WPHeading>

            <IconButton
              onClick={handleCloseDialog}
              className="margin-left-auto closeIcon"
              id="close-license-details"
              tooltip={t("ValueStrings.close")}
            >
              <CloseIcon fillColor="var(--du-color-white)" />
            </IconButton>
          </div>

          <div className="userDetails">
            <div className="profileAvatarContainer">
              <div className="profileAvatar">{initials}</div>
            </div>
            <div className="profileContainer">
              <div className="userName">{userName}</div>
              <div className="userInfo">{userEmail}</div>
              <div className="userCompany">{companyName}</div>
            </div>
          </div>
        </div>

        <div className="headerIllustration">
          <AdvisorIcon className="advisorIcon" />
        </div>
      </div>

      <div className="modalHeaderContainer">
        <div className="modalBodyContainer license-modal-body-container">
          <UserLicenseDetails licenseData={licenses} />
        </div>
      </div>

      {userRole === USER_ROLES.ADMIN && (
        <StyledCustomModal.Footer>
          <WPButton
            variant="outlined"
            id="go-to-license-management"
            onClick={handleGoToLicenseManagement}
          >
            {t("licenseProfile.goToLicenseManagement")}
          </WPButton>
        </StyledCustomModal.Footer>
      )}
    </StyledCustomModal>
  );
};

export default UserSettings;
